import { ApiResult, HttpClient } from "../http.js";
import type { DpaAcceptanceInput } from "./dpa.js";
import {
  AddCompanyMemberResultDto,
  CompanyDto,
  CompanyLockDto,
  CompanyMemberDto,
  SupplierSessionCompanyDto,
  MyCompanyMembershipDto,
  PagedCompanyMembers,
  PendingMemberDto,
} from "../types/companies.js";

export interface CreateCompanyPayload {
  name: string;
  vat: string;
  address: string;
  /** Optional: the DPA version the creator accepted. Omit while no DPA is published. */
  dpaAcceptance?: DpaAcceptanceInput | null;
}

export interface BootstrapCompanyPayload {
  name: string;
  vat: string;
  address: string;
  /** Optional: the DPA version the creator accepted. Omit while no DPA is published. */
  dpaAcceptance?: DpaAcceptanceInput | null;
}

/** Body of POST /api/companies/partner-conversion. CVR is 8 digits. */
export interface ConvertPartnerToCompanyPayload {
  name: string;
  vat: string;
  address: string;
  city?: string | null;
  phone?: string | null;
  email?: string | null;
  /** Optional: the DPA version the creator accepted. Omit while no DPA is published. */
  dpaAcceptance?: DpaAcceptanceInput | null;
}

/** GET /api/companies/partner-conversion — may this caller create a partner-tier company. */
export interface PartnerConversionStatusDto {
  eligible: boolean;
  invitedByCompanyName: string | null;
}

export interface UpdateCompanyPayload {
  name: string;
  vat: string;
  address: string;
  city?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  bankName?: string | null;
  bankAccount?: string | null;
  fiKode?: string | null;
  iban?: string | null;
  swift?: string | null;
  latePaymentInterestRate?: number | null;
  settingsJson?: string | null;
}

export type AddCompanyMemberPayload =
  | {
      userId: string;
      role: "user" | "admin" | "owner";
      email?: never;
      displayName?: never;
    }
  | {
      email: string;
      displayName: string;
      role: "user" | "admin" | "owner";
      userId?: never;
    };

export interface UpdateCompanyMemberPayload {
  role: "user" | "admin" | "owner";
}

/**
 * Employment details, replaced as a whole — an omitted field is cleared, not left
 * alone. Separate from the role payload on purpose: editing a phone number must not
 * require restating the role, which is how a role gets changed by accident.
 *
 * Private email is NOT here. It belongs to the person and follows them across every
 * company, so it is written on the user profile instead of through a company-scoped
 * permission. Next-of-kin is absent pending GDPR review.
 */
export interface UpdateMemberDetailsPayload {
  workPhone?: string | null;
  workEmail?: string | null;
  initials?: string | null;
  employeeNumber?: string | null;
  defaultTimeTypeId?: string | null;
  notes?: string | null;
}

/** Server-side order for a member search; every order ends on user id so paging is stable. */
export type CompanyMemberSort = "name" | "email" | "team" | "joined";

export interface SearchCompanyMembersQuery {
  page?: number;
  pageSize?: number;
  /** Matched server-side against display name and email (and work email). */
  search?: string;
  /** Only members on one of these teams (sent as repeated `teamId`). Empty or unset = all. */
  teamIds?: readonly string[];
  /** Defaults to `name` on the server. */
  sort?: CompanyMemberSort;
  desc?: boolean;
  /** Only these members (sent as repeated `userId`) — resolves a record's preselected people. */
  userIds?: readonly string[];
  /** Leave these members out (sent as repeated `excludeUserId`) — e.g. already participants. */
  excludeUserIds?: readonly string[];
  /** Only members with this role. */
  role?: "owner" | "admin" | "user";
}

export class CompaniesApi {
  constructor(private readonly http: HttpClient) {}

  create(payload: CreateCompanyPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/companies", { body: payload });
  }

  bootstrap(payload: BootstrapCompanyPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/companies/bootstrap", { body: payload });
  }

  partnerConversionStatus(): Promise<ApiResult<PartnerConversionStatusDto>> {
    return this.http.get<PartnerConversionStatusDto>("/api/companies/partner-conversion");
  }

  /** A partner user creates their own partner-tier company. Returns the new org id. */
  convertPartner(payload: ConvertPartnerToCompanyPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/companies/partner-conversion", { body: payload });
  }

  /** Slim company read for a supplier session (403 `supplier_session_only` outside one). */
  supplierSessionCompany(): Promise<ApiResult<SupplierSessionCompanyDto>> {
    return this.http.get<SupplierSessionCompanyDto>("/api/supplier-session/company");
  }

  get(id: string): Promise<ApiResult<CompanyDto>> {
    return this.http.get<CompanyDto>(`/api/companies/${encodeURIComponent(id)}`);
  }

  /**
   * GET /api/companies/current/lock — whether the active company is locked (M9).
   * Reachable while locked; read uncached on the server so a poll sees the
   * unlock as soon as it is committed.
   */
  currentLock(): Promise<ApiResult<CompanyLockDto>> {
    return this.http.get<CompanyLockDto>("/api/companies/current/lock");
  }

  listMembers(id: string): Promise<ApiResult<CompanyMemberDto[]>> {
    return this.http.get<CompanyMemberDto[]>(`/api/companies/${encodeURIComponent(id)}/members`);
  }

  /**
   * Paged, optionally searched (name/email) members — for pickers, which must not load the
   * whole roster on open. `listMembers` above is unchanged and stays the source for callers
   * that need every member (enrichment, permission filtering, recommendation scoring).
   */
  searchMembers(id: string, query: SearchCompanyMembersQuery = {}): Promise<ApiResult<PagedCompanyMembers>> {
    const { teamIds, userIds, excludeUserIds, ...rest } = query;
    return this.http.get<PagedCompanyMembers>(`/api/companies/${encodeURIComponent(id)}/members/search`, {
      query: { ...rest, teamId: teamIds, userId: userIds, excludeUserId: excludeUserIds },
    });
  }

  addMember(id: string, payload: AddCompanyMemberPayload): Promise<ApiResult<AddCompanyMemberResultDto>> {
    return this.http.post<AddCompanyMemberResultDto>(`/api/companies/${encodeURIComponent(id)}/members`, { body: payload });
  }

  myMembership(id: string): Promise<ApiResult<MyCompanyMembershipDto>> {
    return this.http.get<MyCompanyMembershipDto>(`/api/companies/${encodeURIComponent(id)}/members/me`);
  }

  updateMember(id: string, userId: string, payload: UpdateCompanyMemberPayload): Promise<ApiResult<CompanyMemberDto>> {
    return this.http.patch<CompanyMemberDto>(
      `/api/companies/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      { body: payload },
    );
  }

  updateMemberDetails(
    id: string,
    userId: string,
    payload: UpdateMemberDetailsPayload,
  ): Promise<ApiResult<CompanyMemberDto>> {
    return this.http.patch<CompanyMemberDto>(
      `/api/companies/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}/details`,
      { body: payload },
    );
  }

  /** People offered a membership who have not confirmed their email yet. */
  listPendingMembers(id: string): Promise<ApiResult<PendingMemberDto[]>> {
    return this.http.get<PendingMemberDto[]>(`/api/companies/${encodeURIComponent(id)}/pending-members`);
  }

  /** Withdraws a pending offer. */
  cancelPendingMember(id: string, userId: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(
      `/api/companies/${encodeURIComponent(id)}/pending-members/${encodeURIComponent(userId)}`,
    );
  }

  removeMember(id: string, userId: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/companies/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`);
  }

  update(id: string, payload: UpdateCompanyPayload): Promise<ApiResult<CompanyDto>> {
    return this.http.put<CompanyDto>(`/api/companies/${encodeURIComponent(id)}`, { body: payload });
  }

  archive(id: string): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/companies/${encodeURIComponent(id)}/archive`);
  }
}
